import { short_label as shortLabel } from './instant';
import { DEFAULT_CORNER } from '../../util_components/instant_alert/instant_popup_storage';
import { assigned_line as assignedLine } from '../../util_components/instant_alert/instant_alert_entries';
import type { InstantEntry } from '../../util_components/instant_alert/instant_alert_entries';
import { caller_may_claim as callerMayClaim } from '../../util_components/instant_alert/claim';
import type { ClaimCaller } from '../../util_components/instant_alert/claim';
import type { InstantAlertConfig } from '../../util_components/instant_alert/types';
import type { SalesMe } from '../sales_team/api';

// Fired on window when this rep's claim proves a task is no longer pending.
export const INSTANT_GONE_EVENT = 'sales-task:instant-gone';

// Fired on window when this tab's form releases, loses or hands off a claim.
export const SALES_CLAIM_CHANGED_EVENT = 'sales-task:claim-changed';

export const POPUP_STORAGE_KEY_PREFIX = 'sales_task_instant_popup_v1';

// Client-side fallback only; goserver also lets ACTIVE team members claim.
export const SALES_CLAIM_ROLES = ['SALES'];

let last_is_sales: boolean | undefined;

export const record_pending_data = (data: Record<string, unknown>): void => {
  if (typeof data.is_sales === 'boolean') last_is_sales = data.is_sales;
};

export const last_pending_is_sales = (): boolean | undefined => last_is_sales;

export const reset_pending_is_sales = (): void => {
  last_is_sales = undefined;
};

// Server answers first: /me `can_claim`, then the last /instant_pending
// `is_sales`, then the SALES role check (which waits for permissions).
export const sales_may_claim = (
  me: SalesMe | null | undefined,
  pending_is_sales: boolean | undefined,
  caller: ClaimCaller
): boolean => {
  if (typeof me?.can_claim === 'boolean') return me.can_claim;
  if (typeof pending_is_sales === 'boolean') return pending_is_sales;
  return callerMayClaim(caller, SALES_CLAIM_ROLES, { wait_for_permissions: true });
};

// L3 tasks belong to the student's owner and pop up for all L3s; L1/L2 are pool work. Pending rows are
// unclaimed by definition, so no claimer is shown. A row without a level
// predates tiering. Leave status is list-only.
export const sales_meta_line = (entry: InstantEntry): string => {
  const level = entry.level || '';
  if (!level) return assignedLine(entry);
  const head = [level, entry.priority].filter(Boolean).join(' · ');
  if (level === 'L3') return `${head} · owner: ${entry.owner_name || 'No owner'}`;
  return head;
};

export const SALES_INSTANT_ALERT: InstantAlertConfig = {
  pendingPath: '/v2/sales_task_list/instant_pending',
  alertEventPath: '/v2/sales_task_list/alert_event',
  alertEventBody: (uuid, eventType) => ({ task_uuid: uuid, event_type: eventType }),
  listPath: '/sales-task-list',
  formHref: (uuid) => `/sales-task-form?uuid=${encodeURIComponent(uuid)}`,
  relayItemType: 'instant_task',
  relayClearedType: 'instant_task_cleared',
  capabilityKey: 'is_sales',
  activeKey: 'on_shift',
  onPendingData: record_pending_data,
  metaLine: sales_meta_line,
  storagePrefix: POPUP_STORAGE_KEY_PREFIX,
  defaultCorner: DEFAULT_CORNER,
  goneEvent: INSTANT_GONE_EVENT,
  normalize: (raw) => raw as unknown as InstantEntry,
  entryLabel: (entry) => shortLabel(entry.task_type, entry.task_label),
  title: 'Instant Task Alert',
  nounSingular: 'instant task',
  nounPlural: 'instant tasks',
  openLabel: 'Open task →',
  testIdPrefix: 'instant-alert'
};
